import { UserRoundCog, X } from "lucide-react";
import { useEffect, useState } from "react";
import { listManagers } from "../services/api";
import type { ManagerSummary } from "../types/peopleAdmin";

interface ManagerPickerProps {
  division: string;
  value: ManagerSummary | null;
  disabled: boolean;
  onChange: (manager: ManagerSummary | null) => void;
}

export default function ManagerPicker({ division, value, disabled, onChange }: ManagerPickerProps) {
  const [search, setSearch] = useState("");
  const [managers, setManagers] = useState<ManagerSummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (disabled) {
      return;
    }

    let cancelled = false;
    async function loadManagers() {
      try {
        const result = await listManagers(division, search);
        if (!cancelled) {
          setManagers(mergeSelected(result, value));
          setError(null);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : "Could not load managers.");
        }
      }
    }

    const timeout = window.setTimeout(loadManagers, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [division, search, value, disabled]);

  return (
    <section className="inline-tool" aria-label="Manager">
      <div className="inline-tool-heading">
        <UserRoundCog size={18} aria-hidden="true" />
        <h3>Manager</h3>
      </div>
      <div className="manager-grid">
        <label className="field-block">
          <span>Search managers</span>
          <input
            value={search}
            disabled={disabled}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name or email"
          />
        </label>
        <label className="field-block">
          <span>Selected manager</span>
          <select
            value={value?.id || ""}
            disabled={disabled}
            onChange={(event) => {
              const selected = managers.find((manager) => manager.id === event.target.value) || null;
              onChange(selected);
            }}
          >
            <option value="">No manager</option>
            {managers.map((manager) => (
              <option key={manager.id} value={manager.id}>
                {manager.displayName || manager.userPrincipalName}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="icon-only-button" onClick={() => onChange(null)} disabled={disabled || !value} title="Clear manager">
          <X size={18} aria-hidden="true" />
        </button>
      </div>
      {error ? <div className="alert alert-error">{error}</div> : null}
    </section>
  );
}

function mergeSelected(managers: ManagerSummary[], selected: ManagerSummary | null): ManagerSummary[] {
  if (!selected || managers.some((manager) => manager.id === selected.id)) {
    return managers;
  }

  return [selected, ...managers];
}
